import apiService from './api';

class NotificationLogger {
  /**
   * Log notification attempt
   * @param {Object} logData - Notification log details
   */
  async logNotification(logData) {
    const {
      type, // 'invoice', 'sale', 'payment', etc.
      recipientPhone,
      recipientName,
      status, // 'sent', 'failed', 'pending'
      provider, // 'meta', 'twilio', 'gupshup'
      templateName,
      messageId,
      errorMessage,
      metadata // Additional data like invoice_id, sale_id, etc.
    } = logData;

    const logEntry = {
      notification_type: type,
      recipient_phone: recipientPhone,
      recipient_name: recipientName,
      status: status,
      provider: provider,
      template_name: templateName,
      message_id: messageId,
      error_message: errorMessage,
      metadata: JSON.stringify(metadata),
      sent_at: new Date().toISOString(),
      created_at: new Date().toISOString()
    };

    try {
      // Store in localStorage as fallback
      this.storeLocalLog(logEntry);

      // If you have a backend endpoint for logging, call it here
      // const response = await apiService.logNotification(logEntry);
      // return response;

      return logEntry;
    } catch (error) {
      // console.error('Error logging notification:', error);
      // Still store locally even if API fails
      this.storeLocalLog(logEntry);
      throw error;
    }
  }

  /**
   * Store log in localStorage
   */
  storeLocalLog(logEntry) {
    try {
      const logs = this.getLocalLogs();
      logs.push(logEntry);
      
      // Keep only last 1000 logs
      if (logs.length > 1000) {
        logs.shift();
      }
      
      localStorage.setItem('whatsapp_notification_logs', JSON.stringify(logs));
    } catch (error) {
      // console.error('Error storing local log:', error);
    }
  }

  /**
   * Get logs from localStorage
   */
  getLocalLogs() {
    try {
      const logs = localStorage.getItem('whatsapp_notification_logs');
      return logs ? JSON.parse(logs) : [];
    } catch (error) {
      // console.error('Error retrieving local logs:', error);
      return [];
    }
  }

  /**
   * Get logs by type
   */
  getLogsByType(type) {
    const logs = this.getLocalLogs();
    return logs.filter(log => log.notification_type === type);
  }

  /**
   * Get logs by status
   */
  getLogsByStatus(status) {
    const logs = this.getLocalLogs();
    return logs.filter(log => log.status === status);
  }

  /**
   * Get logs by phone number
   */
  getLogsByPhone(phone) {
    const logs = this.getLocalLogs();
    return logs.filter(log => log.recipient_phone === phone);
  }

  /**
   * Get recent logs
   */
  getRecentLogs(limit = 50) {
    const logs = this.getLocalLogs();
    return logs.slice(-limit).reverse();
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    localStorage.removeItem('whatsapp_notification_logs');
  }

  /**
   * Get statistics
   */
  getStatistics() {
    const logs = this.getLocalLogs();
    
    return {
      total: logs.length,
      sent: logs.filter(l => l.status === 'sent').length,
      failed: logs.filter(l => l.status === 'failed').length,
      pending: logs.filter(l => l.status === 'pending').length,
      byType: {
        invoice: logs.filter(l => l.notification_type === 'invoice').length,
        sale: logs.filter(l => l.notification_type === 'sale').length,
        payment: logs.filter(l => l.notification_type === 'payment').length
      }
    };
  }
}

export default new NotificationLogger();
